import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { useOrgUnits } from '../../../../features/org/network/org.api';
import { generalTransactionsHooks } from '../../../../features/accounting/network/general-transactions.api';
import { GeneralTransactionDetailDrawer } from '../../../../features/accounting/components/GeneralTransactionDetailDrawer';

export const Route = createFileRoute('/_auth/accounting/general-transactions/')({
  component: GeneralTransactionsPage,
});

const STATUS_BADGE = {
  DRAFT: { status: 'warning' as const, label: 'Pending Approval' },
  POSTED: { status: 'success' as const, label: 'Posted' },
  REVERSED: { status: 'danger' as const, label: 'Reversed' },
};

function GeneralTransactionsPage() {
  const [page, setPage] = React.useState(1);
  const [type, setType] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [orgUnitId, setOrgUnitId] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const limit = 20;

  const { data, isLoading, isError, refetch } = generalTransactionsHooks.useList({
    page,
    limit,
    type: type || undefined,
    status: status || undefined,
    orgUnitId: orgUnitId || undefined,
  });
  const { data: orgUnitsResponse } = useOrgUnits();
  const branches = (orgUnitsResponse as { id: string; name: string }[] | undefined) ?? [];

  const items = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="General Transactions"
        subtitle="Post cash and journal accounting transactions for maker-checker approval."
        action={
          <Link to="/accounting/general-transactions/new" className="bg-accent-500 text-white px-4 py-2 rounded">
            New Transaction
          </Link>
        }
      />

      <div className="flex flex-wrap gap-4 items-center justify-between mb-6 bg-surface-2 p-4 rounded-xl border border-border">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase">Type</label>
            <select
              value={type}
              onChange={(e) => { setType(e.target.value); setPage(1); }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm"
            >
              <option value="">All Types</option>
              <option value="CASH">Cash</option>
              <option value="JOURNAL">Journal</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase">Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Pending Approval</option>
              <option value="POSTED">Posted</option>
              <option value="REVERSED">Reversed</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase">Branch</label>
            <select
              value={orgUnitId}
              onChange={(e) => { setOrgUnitId(e.target.value); setPage(1); }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
        <Link to="/accounting/journal" className="text-sm text-accent-600 hover:underline font-medium">
          View all posted entries →
        </Link>
      </div>

      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load general transactions</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <div className="bg-surface-0 border border-surface-2 rounded-lg overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-surface-1 border-b border-surface-2">
                  <tr>
                    <th className="py-3 px-4 font-medium text-sm">Date</th>
                    <th className="py-3 px-4 font-medium text-sm">Type</th>
                    <th className="py-3 px-4 font-medium text-sm">Particulars</th>
                    <th className="py-3 px-4 font-medium text-sm">Status</th>
                    <th className="py-3 px-4 font-medium text-sm">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-text-muted">
                        No general transactions found
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-surface-2 last:border-0 hover:bg-surface-50 cursor-pointer"
                        onClick={() => setSelectedId(item.id)}
                      >
                        <td className="py-3 px-4">{new Date(item.entryDate).toLocaleDateString()}</td>
                        <td className="py-3 px-4">{item.type}</td>
                        <td className="py-3 px-4 truncate max-w-md">{item.particulars}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={STATUS_BADGE[item.status].status} label={STATUS_BADGE[item.status].label} />
                        </td>
                        <td className="py-3 px-4 text-text-muted">{new Date(item.createdAt).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {meta && meta.total > limit && (
        <div className="flex items-center justify-between mt-6 bg-surface-2 p-4 rounded-xl border border-border">
          <div className="text-sm font-semibold text-text-secondary">
            Showing page {meta.page} of {Math.max(1, Math.ceil(meta.total / meta.limit))} ({meta.total} items)
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(meta.total / meta.limit)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <GeneralTransactionDetailDrawer transactionId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
